import * as types from './ActionTypes';
import { create } from 'axios';
import { applyOrgFilter } from './filter.action';
import { addToast } from './toast.action';
import _ from 'lodash';

const ajax = create({
  contentType: 'application/json',
  baseURL: process.env.REACT_APP_CORE_USER_API_URL
});

ajax.defaults.withCredentials = true;

export const getOrganizations = () => dispatch => {
  dispatch({
    type: types.GET_ORGANIZATIONS_PENDING
  });

  ajax.get('/organizations?attachRelationships=true')
    .then(resp => {
      const organizations = _.flatten(resp.data.organizations.map(org => {
        if (!org.relationships) {
          org.relationships = [];
        }

        return org.relationships.map(rel => {
          return {
            parentName: org.displayName,
            parentKey: org.externalKey,
            childName: rel.displayName,
            childKey: rel.externalKey,
            childFrom: org.externalKey,
            relationshipType: rel.relationshipType,
            partyType: rel.partyType,
            relationshipKey: rel.relationshipKey,
            landmarkName: rel.landmarkName || 'N/A',
            hierarchy: rel.hierarchy
          };
        }).filter(r => {
          return (
            r.partyType !== 'PERSON' &&
            r.relationshipType !== 'CONTRACTED_CLIENT' &&
            typeof r.childKey !== 'undefined' &&
            r.hierarchy !== 'parent'
          );
        }
        );
      }));

      const clients = _.uniqBy(
        organizations.filter(rel => {
          return rel.relationshipType === 'CLIENT' || rel.relationshipType === 'CONTRACTED_CLIENT' || rel.relationshipType === 'CONTRACTOR';
        }), 'childKey');

      const teams = _.uniqBy(
        organizations.filter(rel => {
          return rel.relationshipType === 'IN_HOUSE_STAFF' || rel.relationshipType === 'CONTRACTED_STAFF' || rel.relationshipType === 'CITY_IN_HOUSE_STAFF';
        }
        ),
        'childKey'
      );
      const payload = {
        data: resp.data.organizations,
        organizations,
        clients,
        teams
      };

      dispatch({
        type: types.GET_ORGANIZATIONS_SUCCESS,
        payload: payload
      });

      dispatch(applyOrgFilter(null));
    })
    .catch(error => {
      dispatch({
        type: types.GET_ORGANIZATIONS_REJECTED,
        payload: error
      });
    });
};

export const getLandmarks = () => dispatch => {
  dispatch({
    type: types.GET_LANDMARKS_PENDING
  });
  ajax.get('/organizations/landmarks')
    .then(data => {
      dispatch({
        type: types.GET_LANDMARKS_SUCCESS,
        payload: data.data.landmarks
      });
    }).catch(error => {
      dispatch({
        type: types.GET_LANDMARKS_REJECTED
      });
    });
};

export const createOrganization = newOrg => async dispatch => {
  try {
    dispatch({
      type: types.CREATE_ORGANIZATION_PENDING
    });
    const postOrg = {
      searchName: newOrg.displayName
        .split(' ')
        .join('_')
        .toUpperCase(),
      displayName: newOrg.displayName,
      relationships: newOrg.multiOrgs.map(x => {
        return {
          fromPartyKey: x.organization.value,
          relationshipType: x.relationshipType.value
        };
      })
    };
    const successToast = `${newOrg.displayName} has been created`;

    const org = await ajax
      .post('/organizations', postOrg);

    if (newOrg.landmarkKey) {
      await ajax.post('/organizations/landmarks', {
        associations: [
          {
            venueTeamKey: org.data.partyKey,
            landmarkKey: newOrg.landmarkKey
          }
        ]
      });
    }
    await dispatch(applyOrgFilter(null));
    await dispatch(getOrganizations());
    await dispatch({
      type: types.CREATE_ORGANIZATION_SUCCESS
    });

    return dispatch(
      addToast({
        autohide: true,
        toasts: [
          {
            action: 'dismiss',
            text: successToast
          }
        ]
      })
    );
  } catch (error) {
    dispatch({
      type: types.CREATE_ORGANIZATION_REJECTED,
      payload: error
    });
    dispatch({
      type: types.SET_ERROR_DIALOG,
      payload: {
        error,
        visible: true
      }
    });
  }
};

export const createOrgRelations = editOrg => dispatch => {
  dispatch({
    type: types.UPDATE_ORGANIZATION_PENDING
  });
  if (editOrg.autoCompleteKey) {
    const autoCompleteRelBody = {
      relationships: editOrg.multiOrgs.map(x => {
        return {
          toPartyKey: editOrg.autoCompleteKey,
          fromPartyKey: x.organization.value,
          relationshipType: x.relationshipType.value
        };
      })
    };
    const successToast = `New relationships for ${editOrg.displayName} have been created`;

    ajax
      .post('/relationships', autoCompleteRelBody)
      .then(() => {
        dispatch(applyOrgFilter(null));
        dispatch(getOrganizations());
      })
      .then(() => {
        return dispatch({
          type: types.UPDATE_ORGANIZATION_SUCCESS
        });
      })
      .then(() => {
        return dispatch(
          addToast({
            autohide: true,
            toasts: [
              {
                action: 'dismiss',
                text: successToast
              }
            ]
          })
        );
      })
      .catch(error => {
        dispatch({
          type: types.UPDATE_ORGANIZATION_REJECTED,
          payload: error
        });
        dispatch({
          type: types.SET_ERROR_DIALOG,
          payload: {
            error,
            visible: true
          }
        });
      });
  }
};

export const updateOrganization = editOrg => dispatch => {
  dispatch({ type: types.UPDATE_ORGANIZATION_PENDING });
  const orgBody = { displayName: editOrg.displayName };
  const newParentOrg = editOrg.organization && editOrg.organization.value;

  const relBody = {
    relationships: [
      {
        relationshipKey: editOrg.relationshipKey,
        fromPartyKey: newParentOrg || editOrg.parentKey,
        toPartyKey: editOrg.childKey,
        relationshipType:
          editOrg.newRelationshipType || editOrg.relationshipType
      }
    ]
  };

  const successToast = `${editOrg.displayName ||
    editOrg.childName} has been updated`;

  if (typeof orgBody.displayName !== 'undefined' && !!editOrg.childKey) {
    ajax.put(`/organizations/${editOrg.childKey}`, orgBody)
      .then(() => ajax.put('/relationships', relBody))
      .then(() => {
        dispatch(applyOrgFilter(null));
        dispatch(getOrganizations());
      })
      .then(() => {
        return dispatch({
          type: types.UPDATE_USER_SUCCESS
        });
      })
      .then(() => {
        return dispatch(
          addToast({
            autohide: true,
            autohideTimeout: 5000,
            toasts: [
              {
                action: 'dismiss',
                text: successToast
              }
            ]
          })
        );
      })
      .catch(error => {
        dispatch({
          type: types.UPDATE_ORGANIZATION_REJECTED,
          payload: error
        });
        dispatch({
          type: types.SET_ERROR_DIALOG,
          payload: {
            error,
            visible: true
          }
        });
      });
  } else {
    ajax.put('/relationships', relBody)
      .then(() => {
        dispatch(applyOrgFilter(null));
        dispatch(getOrganizations());
        dispatch({ type: types.UPDATE_ORGANIZATION_SUCCESS });
        dispatch(addToast({
          toasts: [
            {
              action: 'dismiss',
              text: successToast
            }
          ]
        })
        );
      })
      .catch(error => {
        dispatch({
          type: types.UPDATE_ORGANIZATION_REJECTED,
          payload: error
        });
        dispatch({
          type: types.SET_ERROR_DIALOG,
          payload: {
            error,
            visible: true
          }
        });
      });
  }
};

export const deactivateOrg = org => dispatch => {
  dispatch({ type: types.DELETE_ORGANIZATION_PENDING });
  
  const delBody = { relationships: [org.relationshipKey] };
  const successToast = `${org.childName}'s relationship to ${org.parentName} has been deactivated`;
  ajax.delete('/relationships', { data: delBody })
    .then(() => {
      dispatch({ type: types.DELETE_ORGANIZATION_SUCCESS });
      dispatch(getOrganizations());

      return dispatch(
        addToast({
          autohide: true,
          autohideTimeout: 5000,
          toasts: [
            {
              action: 'dismiss',
              text: successToast
            }
          ]
        })
      );
    })
    .catch(error => {
      dispatch({
        type: types.DELETE_ORGANIZATION_REJECTED,
        payload: error
      });
      dispatch({
        type: types.SET_ERROR_DIALOG,
        payload: {
          error,
          visible: true
        }
      });
    });
};



// WEBPACK FOOTER //
// ./src/actions/organizations.action.js